import * as R from 'ramda';
// features
import PC from '../permission/role-permission';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
//////////////////////////////////////////////////

const defaultSummaryCounts = {
  [GC.DOCUMENT_EXPIRATION_STATUS_VALID]: 0,
  [GC.DOCUMENT_EXPIRATION_STATUS_EXPIRED]: 0,
  [GC.DOCUMENT_EXPIRATION_STATUS_EXPIRING_IN_30_DAYS]: 0,
  [GC.DOCUMENT_EXPIRATION_STATUS_EXPIRING_IN_31_TO_60_DAYS]: 0,
};

const expirationStatusCards = [
  {
    colorPath: 'colors.light.mainRed',
    locale: ['titles:expired', 'Expired'],
    status: GC.DOCUMENT_EXPIRATION_STATUS_EXPIRED,
  },
  {
    colorPath: 'colors.orange',
    status: GC.DOCUMENT_EXPIRATION_STATUS_EXPIRING_IN_30_DAYS,
    locale: ['titles:expiring-in-30-days', 'Expiring in 30 days'],
  },
  {
    colorPath: 'colors.mustardYellow',
    status: GC.DOCUMENT_EXPIRATION_STATUS_EXPIRING_IN_31_TO_60_DAYS,
    locale: ['titles:expiring-in-31-60-days', 'Expiring in 31–60 days'],
  },
  {
    colorPath: 'colors.light.blue',
    locale: ['titles:valid', 'Valid'],
    status: GC.DOCUMENT_EXPIRATION_STATUS_VALID,
  },
];

const getExpirationStatusOptions = () => R.map(
  ({ locale, status }) => ({
    [GC.FIELD_VALUE]: status,
    [GC.FIELD_LABEL]: G.getWindowLocale(...locale),
  }),
  expirationStatusCards,
);

const mapSummaryItems = (data) => R.reduce(
  (acc, subGroup) => {
    const status = R.prop(GC.FIELD_GROUP, subGroup);

    if (G.notHas(status, defaultSummaryCounts)) return acc;

    return R.assoc(status, R.pathOr({}, ['items', 0], subGroup), acc);
  },
  {},
  R.pathOr([], ['subGroups'], data),
);

const tableSettings = {
  minHeight: 340,
  cellFontSize: 13,
  titleFontSize: 14,
  titleRowHeight: 45,
  tableRowHeight: 40,
  allowEditBtn: true,
  checkBoxCellWidth: 0,
  searchableTitles: true,
  allowSelectItems: false,
  checkBoxCellJustifyContent: 'unset',
};

const sharedColumnSettings = {
  [GC.FIELD_ENTERPRISE_NAME]: {
    width: 180,
    searchable: true,
    filterType: 'string',
    name: 'titles:branch',
  },
  [GC.SYSTEM_OBJECT_ASSIGNED_TO_DIVISION]: {
    width: 200,
    searchable: true,
    filterType: 'string',
    name: 'titles:assigned-division',
  },
  [GC.FIELD_DAYS_LEFT]: {
    width: 180,
    searchable: true,
    filterType: 'number',
    name: 'titles:days-left',
  },
  [GC.FIELD_DOCUMENT_SOURCE]: {
    width: 180,
    type: 'enum',
    searchable: true,
    filterType: 'string',
    name: 'titles:document-source',
  },
  [GC.FIELD_EXPIRATION_DATE]: {
    width: 220,
    type: 'date',
    searchable: true,
    filterType: 'date',
    name: 'titles:expiration-date',
  },
  [GC.FIELD_EXPIRATION_STATUS]: {
    width: 180,
    type: 'enum',
    searchable: true,
    filterType: 'string',
    name: 'titles:expiration-status',
    options: getExpirationStatusOptions,
  },
  [GC.FIELD_DOCUMENT_TYPE_NAME]: {
    width: 200,
    searchable: true,
    name: 'titles:document-type',
  },
  [GC.FIELD_SAFETY_SHUTOFF_ENABLED]: {
    width: 180,
    type: 'boolean',
    searchable: true,
    filterType: 'boolean',
    name: 'titles:safety-shutoff',
  },
};

const vehicleStatusColumnSettings = {
  [GC.FIELD_AVAILABLE]: {
    width: 160,
    type: 'boolean',
    searchable: true,
    filterType: 'boolean',
    name: 'titles:available',
  },
  [GC.FIELD_FLEET_IN_SERVICE]: {
    width: 180,
    type: 'boolean',
    searchable: true,
    filterType: 'boolean',
    name: 'titles:in-service',
  },
};

const driverColumnSettings = {
  [GC.FIELD_DRIVER_FULL_NAME]: {
    width: 220,
    searchable: true,
    filterType: 'string',
    useExpandedContainer: true,
    name: 'titles:driver-full-name',
    guidPropName: GC.FIELD_DRIVER_GUID,
    type: GC.PAGE_FLEET_DRIVER_PROFILE_V2,
    cellPermissions: [PC.FLEET_DRIVER_WRITE],
  },
  [GC.FIELD_DRIVER_EMPLOYED]: {
    width: 160,
    type: 'boolean',
    searchable: true,
    filterType: 'boolean',
    name: 'titles:employed',
  },
  [GC.FIELD_DISPATCHING_GROUP_NAME]: {
    width: 200,
    searchable: true,
    name: 'titles:dispatching-group',
  },
  ...sharedColumnSettings,
};

const truckColumnSettings = {
  [GC.FIELD_UNIT_ID]: {
    width: 160,
    searchable: true,
    filterType: 'string',
    name: 'titles:unit-id',
    useExpandedContainer: true,
    guidPropName: GC.FIELD_TRUCK_GUID,
    type: GC.PAGE_FLEET_TRUCK_PROFILE_V2,
    cellPermissions: [PC.FLEET_TRUCK_WRITE],
  },
  ...vehicleStatusColumnSettings,
  [GC.FIELD_DISPATCHING_GROUP_NAME]: {
    width: 200,
    searchable: true,
    name: 'titles:dispatching-group',
  },
  ...sharedColumnSettings,
};

const trailerColumnSettings = {
  [GC.FIELD_UNIT_ID]: {
    width: 160,
    searchable: true,
    filterType: 'string',
    name: 'titles:unit-id',
    useExpandedContainer: true,
    guidPropName: GC.FIELD_TRAILER_GUID,
    type: GC.PAGE_FLEET_TRAILER_PROFILE_V2,
    cellPermissions: [PC.FLEET_TRAILER_WRITE],
  },
  ...vehicleStatusColumnSettings,
  ...sharedColumnSettings,
};

const getDefaultFields = (columnSettings) => R.addIndex(R.map)(
  (name, sequence) => ({ name, sequence }),
  R.keys(columnSettings),
);

const getFilterParams = (columnSettings) => R.compose(
  R.values,
  R.mapObjIndexed(({ name, options, type = 'string', filterType }, value) => ({
    value,
    options,
    type: R.or(filterType, type),
    name: G.getWindowLocale(name),
  })),
)(columnSettings);

const getColumnSettings = (reportType) => {
  if (R.equals(reportType, GC.FLEET_TRUCK_DOCUMENT_VIEW_REPORT)) return truckColumnSettings;

  if (R.equals(reportType, GC.FLEET_TRAILER_DOCUMENT_VIEW_REPORT)) return trailerColumnSettings;

  return driverColumnSettings;
};

const getFilterParamsByReportType = (reportType) => getFilterParams(getColumnSettings(reportType));

const driverDocumentsFilterParams = getFilterParams(driverColumnSettings);
const truckDocumentsFilterParams = getFilterParams(truckColumnSettings);
const trailerDocumentsFilterParams = getFilterParams(trailerColumnSettings);

const driverDocumentsDefaultFields = getDefaultFields(driverColumnSettings);
const truckDocumentsDefaultFields = getDefaultFields(truckColumnSettings);
const trailerDocumentsDefaultFields = getDefaultFields(trailerColumnSettings);

export {
  tableSettings,
  mapSummaryItems,
  getColumnSettings,
  defaultSummaryCounts,
  expirationStatusCards,
  getExpirationStatusOptions,
  truckDocumentsFilterParams,
  getFilterParamsByReportType,
  driverDocumentsFilterParams,
  truckDocumentsDefaultFields,
  trailerDocumentsFilterParams,
  driverDocumentsDefaultFields,
  trailerDocumentsDefaultFields,
};
